/**
 * 服务端受保护文件的展示与拉流：拼接展示用 URL、从 URL 解析记录 ID、
 * 经 `http/apis/system/file` 中的 `fileDownload` / `fileDownloadByUrl` 鉴权拉流并转为 `blob:`（与 HTTP 层仅保留接口定义分离）。
 */
import { fileDownload, fileDownloadByUrl } from '@/http/apis/system/file'

function normalizeApiBasePath(): string {
    const raw = import.meta.env.VITE_API_BASEPATH || '/api'
    const p = (raw.startsWith('/') ? raw : `/${raw}`).replace(/\/$/, '') || '/api'
    return p
}

/** 将相对 API 路径转为绝对 URL（优先 VITE_API_URL，否则当前站点 origin） */
function toAbsoluteApiUrl(relativePath: string): string {
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
    const host = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
    if (host) {
        return `${host}${path}`
    }
    if (typeof window !== 'undefined') {
        return `${window.location.origin}${path}`
    }
    return path
}

/**
 * 根据文件记录 ID 拼接可直接用于展示或 `<img src>` 的资源地址（不含鉴权信息）。
 * 浏览器内优先使用当前页 origin + `/api/...`，与 request 的 baseURL 一致。
 */
export function buildFileSrcUrl(fileId: string | undefined | null): string {
    const id = typeof fileId === 'string' ? fileId.trim() : ''
    if (!id) {
        return ''
    }
    if (/^https?:\/\//i.test(id)) {
        return id
    }
    const basePath = normalizeApiBasePath()
    const relative = `${basePath}/files/${encodeURIComponent(id)}`
    if (typeof window !== 'undefined') {
        return `${window.location.origin}${relative}`
    }
    return toAbsoluteApiUrl(relative)
}

/** 从包含 `/files/{id}` 或 `/files/{id}/download` 的 URL 中解析文件记录 ID */
export function extractFileRecordIdFromFileUrl(url: string): string | null {
    const path = url.split(/[?#]/)[0]
    const m = path.match(/\/files\/([^/]+)(?:\/download)?\/?$/i)
    if (!m?.[1]) {
        return null
    }
    try {
        return decodeURIComponent(m[1])
    } catch {
        return m[1]
    }
}

function unwrapBlobResponse(res: unknown): Blob {
    if (res instanceof Blob) {
        return res
    }
    if (res && typeof res === 'object' && 'data' in res) {
        const data = (res as { data: unknown }).data
        if (data instanceof Blob) {
            return data
        }
        if (data instanceof ArrayBuffer) {
            return new Blob([data])
        }
    }
    throw new Error('无效的文件流响应')
}

/**
 * 按记录 ID 下载文件流，返回可供 `<img :src>` 使用的 `blob:` URL（用毕需 `URL.revokeObjectURL`）。
 */
export async function fetchFileAsObjectUrl(fileRecordId: string): Promise<string> {
    const res = await fileDownload(fileRecordId)
    const blob = unwrapBlobResponse(res as unknown)
    return URL.createObjectURL(blob)
}

/**
 * 按「展示用」地址拉取并转 blob URL；能解析为本系统 `/files/{id}` 时走按 ID 下载，否则走按 URL 下载。
 */
export async function fetchFileBlobObjectUrlBySrc(src: string): Promise<string> {
    const fileId = extractFileRecordIdFromFileUrl(src)
    if (fileId) {
        return fetchFileAsObjectUrl(fileId)
    }
    const res = await fileDownloadByUrl({ url: src })
    const blob = unwrapBlobResponse(res as unknown)
    return URL.createObjectURL(blob)
}

/**
 * @param source 文件记录 ID，或完整 http(s) / 协议相对 / 站内 `/api/.../files/...` 展示地址
 */
export async function fetchAuthFileObjectUrl(source: string): Promise<string> {
    const raw = typeof source === 'string' ? source.trim() : ''
    if (!raw) {
        throw new Error('fetchAuthFileObjectUrl: empty source')
    }
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('/')) {
        return fetchFileBlobObjectUrlBySrc(raw)
    }
    return fetchFileAsObjectUrl(raw)
}
